import { TableDrawer } from "@components/TableDrawer/TableDrawer";
import { useQuery } from "@hooks/useQuery";
import { decodeHTML } from "@utils/CommonUtils";
import { drilldownData, ModuleConfig } from "@utils/drilldownData";
import { Pagination, TableColumnsType, Typography } from "antd";
import axios from "axios";
import { get as _get } from "lodash";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { useNotification } from "~/Hooks/useNotification";
import { post } from "~/Services";
import { centralizedHost } from "~/Utils";
import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import {
  renderActionsColumn,
  renderDateColumn,
  renderImageSasToken,
  renderPublishedColumnStatus,
} from "~/Utils/TableRenderFunction";
import { appendSasToken } from "../../Imagehelper";
import { useGuidelinesForCheckedinLuggageSection2 } from "../TravellersInfo.hook";

export const CentralizedGuidelinesForCheckedInSection2 = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string>("id");
  const [selectedPublished, setSelectedPublished] = useState<any>("");

  const publishedData: TypeData[] = [
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  const {
    data: drilldown,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchDocument,
  } = useGuidelinesForCheckedinLuggageSection2(id || "");

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    const safeData = Array.isArray(data) ? data : [];
    let filteredData = [...safeData];

    if (searchValue) {
      const searchLower = searchValue.toLowerCase();
      filteredData = filteredData.filter((item: any) =>
        String(item.title || "")
          .toLowerCase()
          .includes(searchLower)
      );
    }

    if (selectedPublished !== "") {
      filteredData = filteredData.filter((item: any) => item.published === selectedPublished);
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [data, searchValue, selectedPublished]);

  const getRecords = () => {
    setIsLoading(true);
    let url = `${centralizedHost}/api/baggage/checked-in/section-two`;
    axios
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setTotalRecords(envelopes.length);
          setData(envelopes);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setData([]);
        setIsLoading(false);
      });
  };

  const columns: TableColumnsType = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      width: 100,
      render: renderImageSasToken,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 350,
      render: (value: any) => decodeHTML(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "title"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "title"
            ? "descend"
            : undefined,
    },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      width: 100,
      render: renderPublishedColumnStatus,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "created",
      key: "created",
      render: (value: any) => renderDateColumn(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "created"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "created"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "changed",
      key: "changed",
      render: (value: any, record: any) => {
        if (value && record.created && value === record.created) {
          return "-";
        }
        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "changed"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "changed"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(
              `/centralized/centralizedBaggageGuide/centralizedGuidelinesForCheckedinLuggage/centralizedGuidelinesForCheckedinLuggagePageImage/add?editId=${record?.id}`
            );
          },
          undefined,
          setDeleteModalVisible,
          "users"
        ),
    },
  ];

  useEffect(() => {
    getRecords();
  }, []);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";

    if (sortOrder === "asc") {
      setSortOrder("desc");
      order = "desc";
    } else if (sortOrder === "desc") {
      setSortOrder("asc");
      order = "asc";
    } else if (sortOrder === undefined) {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);

    const tempData = [...data];
    setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action: action,
      method: method,
      module: "Baggage Guide",
      subModule: "centralizedGuidelinesForCheckedinLuggage_centralizedGuidelinesForCheckedinLuggagePageImage",
      type: "CENTRALIZED",
      application: "CENTRALIZED",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "CENTRALIZED",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...data,
        published: true,
      },
      status: status,
    };

    post("/management/publish", payload)
      .then((response) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        navigate("/centralized/status-information?activeTab=pending");
      })
      .catch((err: any) => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  async function sendDeleteAwardsRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${centralizedHost}/api/baggage/checked-in/section-two/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {}
  }

  const imageKeys = ["image"];

  const drilldownAny = drilldown?.[0] as any;

  const dataWithSas = {
    ...drilldown?.[0],
    ...Object.fromEntries(imageKeys.map((key) => [key, drilldownAny?.[key] ? appendSasToken(drilldownAny[key]) : ""])),
  };

  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  useEffect(() => {
    setSelectedPublished("");
    setSearchValue("");
    setCurrentPage(0);
  }, []);

  return (
    <>
      <DataTable
        tabOffset={0}
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={
          currentPageData
            ? currentPageData?.map((d: any) => {
                return {
                  ...d,
                  title: decodeHTML(d.title),
                };
              })
            : []
        }
        isLoading={isLoading}
        exportFileName="CONTRACT"
        isPagination={true}
        recordsPerPage={10}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search Title"}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={handlePublishedChange}
              placeholder="Select Published"
              className="w-48"
            />
          </div>
        }
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          // total={totalRecords}
          total={sortedData.length}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          // onChange={(curPage) => {
          //   setCurrentPage(curPage - 1);
          // }}
        />
      </div>

      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={
          drilldownData[
            _get(drilldown, "subModule", "centralizedGuidelinesForCheckedinLuggagePageImage") as ModuleConfig
          ]
        }
        data={dataWithSas}
      />

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteAwardsRequest}
        defaultReviewNotes="Kindly review this Baggage Guide"
      />
    </>
  );
};
function handleSort(a: any, b: any, order: string, sortBy: string) {
  if (sortBy === "title") {
    const titleA = a[sortBy]?.toLowerCase() || "";
    const titleB = b[sortBy]?.toLowerCase() || "";

    if (order === "desc") {
      return titleA < titleB ? 1 : -1;
    } else if (order === "asc") {
      return titleA > titleB ? 1 : -1;
    } else {
      return 0;
    }
  }

  if (sortBy === "field_faqs_question") {
    const numberA = extractNumber(a[sortBy]);
    const numberB = extractNumber(b[sortBy]);

    if (order === "desc") {
      return numberA < numberB ? 1 : -1;
    } else if (order === "asc") {
      return numberA > numberB ? 1 : -1;
    } else {
      return 0;
    }
  }

  if (order === "desc") {
    return a[sortBy] < b[sortBy] ? 1 : -1;
  } else if (order === "asc") {
    return a[sortBy] > b[sortBy] ? 1 : -1;
  } else {
    return 0;
  }
}
function extractNumber(question: string): number {
  const match = question.match(/Q(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}
